'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronLeft, Zap, CheckCircle, Edit3, Download, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useChat } from '@/lib/hooks/useChat';
import { ApiErrorMessage } from '@/components/ui/ErrorBoundary';
import type { WizardData } from './ContentGeneratorWizard';

interface Props {
  data: WizardData;
  onBack: () => void;
}

interface GeneratedPost {
  id: string;
  day: number;
  platform: string;
  format: string;
  title: string;
  content: string;
  status: 'pending' | 'generating' | 'generated' | 'error';
  words: number;
}

const platformColors: Record<string, string> = {
  LI: '#0A66C2',
  IG: '#C13584',
  X: '#aaa',
  FB: '#1877F2',
  TT: '#010101',
  BLOG: '#FF5F1F',
};

const platformLabels: Record<string, string> = {
  LI: 'LinkedIn',
  IG: 'Instagram',
  X: 'X',
  FB: 'Facebook',
  TT: 'TikTok',
  BLOG: 'Blog',
};

const platformTones: Record<string, string> = {
  LI: 'professionell, thought-leadership, sachlich mit persönlicher Note',
  IG: 'visuell, emotional, hashtag-freundlich, Story-orientiert',
  X: 'prägnant, viral, provokativ, max. 280 Zeichen pro Tweet',
  FB: 'community-orientiert, humorvoll, satirisch, engagement-fokussiert',
  TT: 'hochenergetisch, POV-Stil, rebellisch, Gen-Z-Sprache, Hook in Sekunde 1',
  BLOG: 'SEO-optimiert, informativ, strukturiert, 1500+ Wörter',
};

const platformRules: Record<string, string> = {
  LI: '- Länge: 800–1.500 Zeichen\n- Beginne mit einem starken Hook (erste Zeile entscheidet)\n- Nutze Zeilenumbrüche für Lesbarkeit\n- Kein übermäßiger Hashtag-Einsatz (max. 3–5)\n- Persönliche Anekdote oder Datenpunkt als Einstieg',
  IG: '- Caption: 150–300 Zeichen + 10–20 relevante Hashtags\n- Beginne mit Emoji oder starkem Satz\n- Hashtags am Ende in einem Block\n- Für Reels: Klarer Hook in Zeile 1, CTA am Ende\n- Für Carousel: Slide 1 = Hook, Slide 10 = CTA',
  X: '- Jeder Tweet: MAXIMAL 280 Zeichen\n- Thread: 5–10 Tweets, nummeriert (1/n)\n- Erster Tweet = stärkste Aussage\n- Keine Füllwörter, jedes Wort zählt\n- Provokative Frage oder steile These als Opener',
  FB: '- Länge: 200–500 Zeichen für organische Reichweite\n- Frage am Ende für Kommentare\n- Emojis sparsam einsetzen\n- Community-Bezug herstellen\n- Humor und Ironie erlaubt',
  TT: '- Script-Format: Hook (0–3 Sek) → Problem → Lösung → CTA\n- Gesprochene Sprache, kurze Sätze\n- POV-Stil: "POV: Du bist..."\n- Trending-Sounds erwähnen wenn passend\n- Länge: 30–60 Sekunden Sprechtext',
  BLOG: '- Mindestlänge: 1.500 Wörter\n- H2/H3-Struktur mit Keywords\n- Einleitung: Problem → Lösung versprechen\n- Fazit mit klarem CTA\n- Meta-Description am Ende (155 Zeichen)',
};

const brandToneDescriptions: Record<string, { desc: string; example: string; traits: string }> = {
  'Provokativ': {
    desc: 'Direkt, reibend, polarisierend. Sagt, was andere nicht aussprechen. Keine Weichspüler.',
    example: '"Die meisten LinkedIn-Posts sind Selbstgespräche. Niemand interessiert das."',
    traits: 'Nutze steile Thesen, direkte Aussagen, provokative Fragen. Vermeide Weichspüler-Formulierungen wie "Es könnte sein" oder "Vielleicht". Polarisiere bewusst.',
  },
  'Authentisch': {
    desc: 'Persönlich, ehrlich, nahbar. Echte Geschichten statt glatter Oberflächen.',
    example: '"Ich erinnere mich noch genau an den Tag, als wir fast alles verloren hätten."',
    traits: 'Schreibe in der Ich-Perspektive, teile echte Erfahrungen und Fehler. Vermeide Corporate-Sprache. Sei verletzlich und ehrlich.',
  },
  'Human-Centric': {
    desc: 'Empathisch, lösungsorientiert, auf Augenhöhe. Der Mensch steht im Mittelpunkt.',
    example: '"Was brauchst du wirklich, um morgen besser zu starten als heute?"',
    traits: 'Stelle den Leser in den Mittelpunkt, nicht das Produkt. Nutze "Du"-Ansprache. Fokus auf Mehrwert und Problemlösung. Empathische Sprache.',
  },
  'Business-Punk': {
    desc: 'Rebellisch, antikonform, mit Substanz. Gegen den Strom, aber mit Strategie.',
    example: '"Vergiss den Businessplan. Bau zuerst das Ding. Erkläre es danach."',
    traits: 'Breche Konventionen, hinterfrage Mainstream-Meinungen, nutze unerwartete Metaphern. Substanz über Style. Rebellisch aber nicht respektlos.',
  },
};

const formatMap: Record<string, string[]> = {
  LI: ['Experten-Artikel', 'Storytelling-Post', 'Thought Leadership', 'Case Study', 'Newsletter-Auszug'],
  IG: ['Caption + Hashtags', 'Reel-Script', '10-Slide Carousel', 'Story-Sequenz', 'Caption'],
  X: ['Viraler Thread', 'Duct Tape Statement', 'Meinungs-Tweet', 'Frage-Thread', 'Viraler Thread'],
  FB: ['Community-Post', 'Satire-Post', 'Business-Fail', 'Veranstaltungs-Post', 'Community-Post'],
  TT: ['POV-Script', 'Rebel-Style Video', 'Hook-Script', 'POV-Script', 'Trend-Script'],
  BLOG: ['SEO-Artikel', 'Ratgeber', 'Analyse', 'SEO-Artikel', 'Listicle'],
};

function buildPostPlan(platforms: string[], postCount: number): GeneratedPost[] {
  const posts: GeneratedPost[] = [];
  let postNum = 0;
  for (let day = 1; day <= 14 && postNum < postCount; day++) {
    const platsForDay = platforms.filter((_, idx) => (day + idx) % 3 !== 0 || idx === 0);
    for (const plat of platsForDay.slice(0, 2)) {
      if (postNum >= postCount) break;
      const tIdx = postNum % 5;
      const fmts = formatMap[plat] ?? ['Post'];
      posts.push({
        id: `gen-${String(postNum + 1).padStart(3, '0')}`,
        day,
        platform: plat,
        format: fmts[tIdx] ?? 'Post',
        title: '',
        content: '',
        status: 'pending',
        words: 0,
      });
      postNum++;
    }
  }
  return posts;
}

function buildGeminiPrompt(post: GeneratedPost, data: WizardData): string {
  const platformTone = platformTones[post.platform] ?? 'professionell';
  const platformLabel = platformLabels[post.platform] ?? post.platform;
  const rules = platformRules[post.platform] ?? '- Klarer, prägnanter Text\n- Starker Hook';
  const brandTone = brandToneDescriptions[data.tone] ?? brandToneDescriptions['Business-Punk'];

  return `Du bist ein professioneller Content-Stratege für den deutschen Mittelstand. Erstelle einen ${post.format} für ${platformLabel}.

## BRAND VAULT — TONALITÄT
Gewählter Ton: **${data.tone}**
Beschreibung: ${brandTone.desc}
Schreibweise: ${brandTone.traits}
Beispiel-Stil: ${brandTone.example}

## PLATTFORM-KONTEXT: ${platformLabel}
Plattform-Ton: ${platformTone}
Plattform-Regeln:
${rules}

## AUSGANGSINHALT
Projekt: ${data.projectName || 'Unbekannt'}
Kalender-Tag: ${post.day} von 14

${data.inputContent.slice(0, 1500)}

## AUFGABE
Erstelle einen ${post.format} für ${platformLabel}.
- Sprache: Deutsch
- Halte dich STRIKT an die Plattform-Regeln oben
- Wende den Brand-Ton "${data.tone}" konsequent an: ${brandTone.traits}
- Beginne mit einem starken Hook
- Keine generischen Phrasen, kein Buzzword-Bingo
- Kein Metakommentar — gib NUR den fertigen Post-Text aus`;
}

/* ── Skeleton card shown while a post is pending/generating ── */
function PostSkeleton({ platform, day, isGenerating }: { platform: string; day: number; isGenerating: boolean }) {
  return (
    <div className="punk-card p-4 animate-pulse">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-5 w-10 rounded bg-surface-2" />
        <div className="h-4 w-24 rounded bg-surface-2" />
        {isGenerating && (
          <div className="ml-auto flex items-center gap-1.5">
            <Loader2 size={12} className="text-accent animate-spin" />
            <span className="text-xs mono text-accent">Generiert…</span>
          </div>
        )}
      </div>
      <div className="space-y-2 mb-3">
        <div className="h-3 w-3/4 rounded bg-surface-2" />
        <div className="h-3 w-full rounded bg-surface-2" />
        <div className="h-3 w-5/6 rounded bg-surface-2" />
        <div className="h-3 w-2/3 rounded bg-surface-2" />
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex gap-2">
          <div className="h-3 w-12 rounded bg-surface-2" />
          <div className="h-3 w-16 rounded bg-surface-2" />
        </div>
        <div className="h-3 w-20 rounded bg-surface-2" />
      </div>
    </div>
  );
}

export default function WizardStep3Generate({ data, onBack }: Props) {
  const [generating, setGenerating] = useState(false);
  const [posts, setPosts] = useState<GeneratedPost[]>([]);
  const [done, setDone] = useState(false);
  const [currentGenIdx, setCurrentGenIdx] = useState(-1);
  const [progress, setProgress] = useState(0);
  const [apiError, setApiError] = useState<string | null>(null);

  const { response, isLoading, error, sendMessage } = useChat('GEMINI', 'gemini/gemini-2.5-flash', false);

  const targetPosts = buildPostPlan(data.platforms, data.postCount);

  useEffect(() => {
    if (error) {
      toast.error('Generierung fehlgeschlagen: ' + error.message);
      setApiError(error.message ?? 'Die Verbindung zur KI-API ist fehlgeschlagen. Bitte prüfe deine Internetverbindung und versuche es erneut.');
      setGenerating(false);
      // Mark current generating post as error
      if (currentGenIdx >= 0) {
        setPosts((prev) =>
          prev.map((p, i) => (i === currentGenIdx ? { ...p, status: 'error' } : p))
        );
      }
    }
  }, [error]);

  useEffect(() => {
    if (!isLoading && response && currentGenIdx >= 0 && generating) {
      const updatedContent = response;
      const titleMatch = updatedContent.split('\n')[0]?.replace(/^#+\s*/, '').slice(0, 80) || `${platformLabels[posts[currentGenIdx]?.platform]} Post Tag ${posts[currentGenIdx]?.day}`;

      setPosts((prev) =>
        prev.map((p, i) =>
          i === currentGenIdx
            ? {
                ...p,
                status: 'generated',
                content: updatedContent,
                title: titleMatch,
                words: updatedContent.split(/\s+/).length,
              }
            : p
        )
      );

      const nextIdx = currentGenIdx + 1;
      const pct = Math.round((nextIdx / targetPosts.length) * 100);
      setProgress(pct);

      if (nextIdx < targetPosts.length) {
        setCurrentGenIdx(nextIdx);
        setPosts((prev) => prev.map((p, i) => (i === nextIdx ? { ...p, status: 'generating' } : p)));
        const nextPost = targetPosts[nextIdx];
        sendMessage([{ role: 'user', content: buildGeminiPrompt(nextPost, data) }], {
          temperature: 0.8,
          max_tokens: 800,
        });
      } else {
        setGenerating(false);
        setDone(true);
        toast.success(`${targetPosts.length} Beiträge erfolgreich generiert!`, {
          description: 'Dein 14-Tage-Kalender ist bereit.',
        });
      }
    }
  }, [isLoading, response]);

  const startGeneration = () => {
    if (!data.inputContent.trim() && data.inputType === 'text') {
      toast.error('Bitte gib zuerst einen Ausgangstext ein.');
      return;
    }
    setApiError(null);
    const initialPosts = targetPosts.map((p) => ({ ...p, status: 'pending' as const }));
    setPosts(initialPosts);
    setGenerating(true);
    setProgress(0);
    setCurrentGenIdx(0);

    setPosts((prev) => prev.map((p, i) => (i === 0 ? { ...p, status: 'generating' } : p)));
    sendMessage([{ role: 'user', content: buildGeminiPrompt(targetPosts[0], data) }], {
      temperature: 0.8,
      max_tokens: 800,
    });
  };

  const platformBadgeClass: Record<string, string> = {
    LI: 'platform-badge-li',
    IG: 'platform-badge-ig',
    X: 'platform-badge-x',
    FB: 'platform-badge-fb',
    TT: 'platform-badge-tt',
    BLOG: 'platform-badge-blog',
  };

  return (
    <div>
      {/* API Error Banner */}
      {apiError && (
        <div className="mb-4">
          <ApiErrorMessage
            title="Generierung fehlgeschlagen"
            description={apiError}
            onRetry={() => {
              setApiError(null);
              setPosts([]);
              setDone(false);
              setCurrentGenIdx(-1);
              setProgress(0);
            }}
          />
        </div>
      )}

      {/* Controls */}
      {!done && (
        <div className="punk-card p-5 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-700 text-white">Bereit zur Generierung</h3>
              <p className="text-xs text-muted mt-1">
                <span className="mono text-accent">{targetPosts.length} Beiträge</span> für{' '}
                <span className="mono text-white">{data.platforms.map((p) => platformLabels[p] ?? p).join(', ')}</span> ·{' '}
                Ton: <span className="mono text-accent">{data.tone}</span>
              </p>
            </div>
            {!generating && (
              <button onClick={startGeneration} className="btn-primary">
                <Zap size={14} />
                Jetzt generieren
              </button>
            )}
            {generating && (
              <div className="flex items-center gap-2 text-xs mono text-accent">
                <Loader2 size={14} className="animate-spin" />
                Läuft…
              </div>
            )}
          </div>

          {(generating || done) && (
            <div>
              <div className="flex justify-between text-xs mono mb-2">
                <span className="text-muted">Generierungsfortschritt</span>
                <span className="text-accent">{progress}%</span>
              </div>
              <div className="h-1.5 bg-surface-2 rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              {generating && (
                <p className="text-xs text-muted mt-2 mono pulse-orange">
                  Gemini generiert deutschen Content… ({currentGenIdx + 1}/{targetPosts.length})
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {posts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="section-label">Generierter 14-Tage-Kalender</p>
            {done && (
              <div className="flex gap-2">
                <Link href="/content-editor" className="btn-ghost text-xs">
                  <Edit3 size={12} />
                  Im Editor öffnen
                </Link>
                <button
                  onClick={() => toast.success('ZIP wird erstellt…')}
                  className="btn-primary text-xs"
                >
                  <Download size={12} />
                  Alle exportieren
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {posts.map((post, idx) => {
              /* Show skeleton for pending/generating posts */
              if (post.status === 'pending' || post.status === 'generating') {
                return (
                  <PostSkeleton
                    key={post.id}
                    platform={post.platform}
                    day={post.day}
                    isGenerating={post.status === 'generating'}
                  />
                );
              }

              const badgeCls = platformBadgeClass[post.platform] ?? '';
              return (
                <div
                  key={post.id}
                  className={`punk-card p-4 fade-in ${post.status === 'error' ? 'border-red-800/50' : ''}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`status-badge ${badgeCls}`}>{post.platform}</span>
                    <span className="text-xs text-muted truncate">{post.format}</span>
                    {post.status === 'generated' && (
                      <CheckCircle size={12} className="text-green-400 ml-auto flex-shrink-0" />
                    )}
                    {post.status === 'error' && (
                      <AlertCircle size={12} className="text-red-400 ml-auto flex-shrink-0" />
                    )}
                  </div>
                  <h3 className="text-xs font-600 text-white leading-snug mb-2 line-clamp-2">{post.title}</h3>
                  <p className="text-xs text-muted leading-relaxed line-clamp-4 whitespace-pre-line">{post.content}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                    <div className="flex items-center gap-2">
                      <span className="mono text-xs text-muted">Tag {post.day}</span>
                      <span className="text-muted">·</span>
                      <span className="mono text-xs text-muted">{post.words} Wörter</span>
                    </div>
                    <Link href="/content-editor" className="text-xs text-accent hover:underline mono">
                      Bearbeiten →
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Back button */}
      {!generating && (
        <div className="mt-6">
          <button onClick={onBack} className="btn-ghost">
            <ChevronLeft size={14} />
            Zurück
          </button>
        </div>
      )}
    </div>
  );
}